import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { fetchMemberOverduePayments, createStripeCheckoutSession } from '../../actions';

// Initialize Stripe - Replace with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51...');

function OverduePayments({ fetchMemberOverduePayments, createStripeCheckoutSession, auth, compact = false }) {
    const mounted = useRef(false);
    const [overdueContracts, setOverdueContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState({});
    const [error, setError] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const popupRef = useRef(null);

    useEffect(() => {
        mounted.current = true;
        loadOverduePayments();
        return () => (mounted.current = false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadOverduePayments = async () => {
        try {
            setLoading(true);
            const response = await fetchMemberOverduePayments({});
            if (response && response.status === 200 && response.data) {
                if (mounted.current) {
                    setOverdueContracts(response.data.contracts || []);
                }
            } else {
                if (mounted.current) {
                    setError('Failed to load overdue payments');
                }
            }
        } catch (err) {
            console.error('Error loading overdue payments:', err);
            if (mounted.current) {
                setError('Error loading overdue payments');
            }
        } finally {
            if (mounted.current) {
                setLoading(false);
            }
        }
    };

    const handlePayNow = async (contract) => {
        if (processing[contract.contractId]) return;

        try {
            setProcessing(prev => ({ ...prev, [contract.contractId]: true }));
            setError(null);

            // Create checkout session
            const response = await createStripeCheckoutSession({
                contractId: contract.contractId,
                amount: contract.overdueAmount
            });

            if (response && response.status === 200 && response.data) {
                // Redirect to Stripe Checkout
                const stripe = await stripePromise;
                if (stripe && response.data.url) {
                    window.location.href = response.data.url;
                } else {
                    throw new Error('Stripe not initialized or checkout URL missing');
                }
            } else {
                throw new Error(response?.data?.message || 'Failed to create checkout session');
            }
        } catch (err) {
            console.error('Error creating checkout session:', err);
            setError(err.message || 'Failed to initiate payment');
        } finally {
            setProcessing(prev => {
                const newState = { ...prev };
                delete newState[contract.contractId];
                return newState;
            });
        }
    };

    // Check for payment success/cancellation in URL params
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const paymentStatus = urlParams.get('payment');

        if (paymentStatus === 'success') {
            // Reload overdue payments after successful payment
            setTimeout(() => {
                loadOverduePayments();
                // Remove query params from URL
                window.history.replaceState({}, document.title, window.location.pathname);
            }, 1000);
        } else if (paymentStatus === 'cancelled') {
            // Remove query params from URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close popup when clicking outside
    useEffect(() => {
        if (!compact) return;
        
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setShowPopup(false);
            }
        };
        
        if (showPopup) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showPopup, compact]);

    // COMPACT MODE - Small button with popup
    if (compact) {
        const hasOverdue = overdueContracts.length > 0;
        
        return (
            <div className="relative" ref={popupRef}>
                {/* Compact Button */}
                <button
                    onClick={() => setShowPopup(!showPopup)}
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all shadow-sm hover:shadow-md ${
                        hasOverdue 
                            ? 'bg-red-50 text-red-700 border-2 border-red-300 hover:bg-red-100' 
                            : 'bg-green-50 text-green-700 border-2 border-green-300 hover:bg-green-100'
                    }`}
                    title={hasOverdue ? 'Overdue Payments' : 'No Overdue Payments'}
                >
                    {/* Icon */}
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                        <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                    </svg>
                    <span className="text-sm">Payments</span>
                    {/* Red badge if overdue */}
                    {hasOverdue && (
                        <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold">
                            {overdueContracts.length}
                        </span>
                    )}
                </button>

                {/* Popup */}
                {showPopup && (
                    <div className="absolute left-0 top-full mt-2 w-96 bg-white rounded-lg shadow-2xl border-2 border-gray-200 z-50 max-h-[32rem] overflow-y-auto">
                        <div className="p-4">
                            <h3 className={`text-lg font-bold mb-3 ${hasOverdue ? 'text-red-600' : 'text-green-700'}`}>
                                {hasOverdue ? '⚠️ Overdue Payments' : '✅ Payments Up to Date'}
                            </h3>
                            
                            {loading && (
                                <div className="text-gray-500 text-center py-4">Loading...</div>
                            )}
                            
                            {error && !loading && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 mb-3">
                                    {error}
                                </div>
                            )}
                            
                            {!loading && !hasOverdue && (
                                <div className="text-green-700 py-2">
                                    No overdue payments at this time. All accounts are up to date!
                                </div>
                            )}
                            
                            {!loading && hasOverdue && (
                                <div className="space-y-3">
                                    {overdueContracts.map((contract) => (
                                        <div
                                            key={contract.contractId}
                                            className="border-2 border-red-200 rounded-lg p-3 bg-red-50"
                                        >
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h4 className="font-semibold text-gray-800 text-sm">
                                                        {contract.studentName}
                                                    </h4>
                                                    <div className="text-xs text-gray-600">
                                                        ID: {contract.contractId}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-xl font-bold text-red-600">
                                                        ${contract.overdueAmount.toFixed(2)}
                                                    </div>
                                                    <div className="text-xs text-gray-500">Overdue</div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                                                <div>
                                                    <span className="text-gray-600">Expected:</span>
                                                    <span className="ml-1 font-medium">${contract.expectedAmount.toFixed(2)}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Paid:</span>
                                                    <span className="ml-1 font-medium">${contract.paidAmount.toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handlePayNow(contract)}
                                                disabled={processing[contract.contractId]}
                                                className="w-full bg-mpBlue hover:bg-mpTeal text-white font-semibold py-2 px-3 rounded text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {processing[contract.contractId] ? 'Processing...' : 'Pay Now'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // FULL MODE - Original large cards (fallback)
    if (loading) {
        return (
            <div className="card w-full mb-6">
                <div className="p-4">
                    <h2 className="text-xl font-semibold mb-4">Overdue Payments</h2>
                    <div className="text-gray-500">Loading...</div>
                </div>
            </div>
        );
    }

    if (error && overdueContracts.length === 0) {
        return (
            <div className="card w-full mb-6">
                <div className="p-4">
                    <h2 className="text-xl font-semibold mb-4">Overdue Payments</h2>
                    <div className="text-red-500">{error}</div>
                </div>
            </div>
        );
    }

    if (overdueContracts.length === 0 && !loading && !error) {
        return (
            <div className="card w-full mb-6 border-2 border-green-200 bg-green-50">
                <div className="p-4">
                    <h2 className="text-xl font-semibold mb-2 text-green-700 flex items-center">
                        <span className="mr-2">✅</span>
                        Overdue Payments
                    </h2>
                    <div className="text-green-700 font-medium">No overdue payments at this time. All accounts are up to date!</div>
                </div>
            </div>
        );
    }

    return (
        <div className="card w-full mb-6">
            <div className="p-4">
                <h2 className="text-xl font-semibold mb-4 text-red-600">Overdue Payments</h2>
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
                        {error}
                    </div>
                )}
                <div className="space-y-4">
                    {overdueContracts.map((contract) => (
                        <div
                            key={contract.contractId}
                            className="border-2 border-red-200 rounded-lg p-4 bg-white"
                        >
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800">
                                        {contract.studentName}
                                    </h3>
                                    <div className="text-sm text-gray-600 mt-1">
                                        Contract ID: {contract.contractId}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-bold text-red-600">
                                        ${contract.overdueAmount.toFixed(2)}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">Overdue</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                                <div>
                                    <span className="text-gray-600">Expected:</span>
                                    <span className="ml-2 font-medium">${contract.expectedAmount.toFixed(2)}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Paid:</span>
                                    <span className="ml-2 font-medium">${contract.paidAmount.toFixed(2)}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => handlePayNow(contract)}
                                disabled={processing[contract.contractId]}
                                className="w-full bg-mpBlue hover:bg-mpTeal text-white font-semibold py-2 px-4 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {processing[contract.contractId] ? 'Processing...' : 'Pay Now'}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps, {
    fetchMemberOverduePayments,
    createStripeCheckoutSession
})(OverduePayments);

